import { put, takeEvery, call } from "redux-saga/effects";

import * as apis from "../../network/apis/user";
import {
  getDriverPointsListResponse,
  getDriverRewardsListResponse,
  getUserDetailsResponse,
  getUsersListResponse,
  getTeamDriversListResponse,
  getUsersListRequest,
  setRejectedDriversData,
  getPendingDriversResponse,
  getPendingDriversRequest,
  getDriverRequestsListResponse,
  getDriverRequestsListRequest,
  getUserDetailsRequest,
  getReferralRewardsListResponse,
  verifyDriverLicenseResponse,
  getVehiclesListResponse,
} from "./slice";
import { showSnackMsg } from "../../utils/Helpers";
import { setCanShowEmptyState, setSkeletonObject } from "../Loader/slice";
import { USERS_ROLES } from "../../utils/Constants";

export function* getUsersListSaga({ payload }) {
  try {
    yield put(getUsersListResponse({}));
    yield put(setSkeletonObject({ field: "table", value: true }));
    const response = yield call(
      payload.role === USERS_ROLES.driver ? apis.getDrivers : apis.getOwners,
      {
        ...payload,
        role: null,
      }
    );

    const meta = {
      totalUsers:
        payload.role === USERS_ROLES.driver
          ? response.data.drivers_total_count
          : response.headers["total-count"],
      currentPage: +response.headers["current-page"],
      count: +response.headers["total-count"],
      pages: +response.headers["total-pages"],
      totalQualifiedDrivers: response.data.qualified_total_count,
      totalStandardDrivers: response.data.standard_total_count,
      totalTrips:
        response.data[
          payload.role === USERS_ROLES.driver
            ? "trips_count"
            : "total_number_of_trips"
        ],
      totalKms:
        response.data[
          payload.role === USERS_ROLES.driver
            ? "total_covered_kms"
            : "total_number_of_kms_covered"
        ],
      totalMaintenances:
        response.data[
          payload.role === USERS_ROLES.driver
            ? "maintenances_logged_count"
            : "total_number_of_maintenances_logged"
        ],
      totalVehicles:response.data.total_vehicles_count,
      totalCompletedProfiles:response.data.total_profile_completed_count,
      totalDeletedLogs: response.data.deleted_maintenances_count,
    };
    const users =
      response.data?.[
        payload.role === USERS_ROLES.driver ? "drivers" : "owners"
      ];

    yield put(getUsersListResponse({ users, meta }));
    yield put(setSkeletonObject({ field: "table", value: false }));
    if (users.length === 0) {
      yield put(setCanShowEmptyState(true));
    } else {
      yield put(setCanShowEmptyState(false));
    }
  } catch (err) {
    yield put(setSkeletonObject({ field: "table", value: false }));
    yield put(setCanShowEmptyState(true));
    yield err.response?.status !== 401 &&
      showSnackMsg(err.response?.data?.message);
  }
}

export function* getUserDetailsSaga({ payload }) {
  try {
    yield put(setSkeletonObject({ field: "userDetails", value: true }));
    const response = yield call(
      payload.role === USERS_ROLES.driver
        ? apis.getDriverDetails
        : apis.getOwnerDetails,
      payload.id
    );
    const user =
      response.data?.[payload.role === USERS_ROLES.driver ? "driver" : "owner"];
    yield put(getUserDetailsResponse(user));
    yield put(setSkeletonObject({ field: "userDetails", value: false }));
    if (!user) {
      yield put(setCanShowEmptyState(true));
    } else {
      yield put(setCanShowEmptyState(false));
    }
  } catch (err) {
    yield put(setSkeletonObject({ field: "userDetails", value: false }));
    yield put(setCanShowEmptyState(true));
    yield err.response?.status !== 401 &&
      showSnackMsg(err.response?.data?.message);
  }
}

export function* getTeamDriversListSaga({ payload }) {
  try {
    yield put(getTeamDriversListResponse({}));
    yield put(setSkeletonObject({ field: "table", value: true }));
    const response = yield call(apis.getTeamDrivers, payload);
    const meta = {
      currentPage: +response.headers["current-page"],
      count: +response.headers["total-count"],
      pages: +response.headers["total-pages"],
    };
    const drivers = response.data?.drivers;
    yield put(getTeamDriversListResponse({ drivers, meta }));
    yield put(setSkeletonObject({ field: "table", value: false }));
    if (drivers.length === 0) {
      yield put(setCanShowEmptyState(true));
    } else {
      yield put(setCanShowEmptyState(false));
    }
  } catch (err) {
    yield put(setSkeletonObject({ field: "table", value: false }));
    yield put(setCanShowEmptyState(true));
    yield err.response?.status !== 401 &&
      showSnackMsg(err.response?.data?.message);
  }
}

export function* getDriverPointsListSaga({ payload }) {
  try {
    yield put(getDriverPointsListResponse({}));
    yield put(setSkeletonObject({ field: "pointsTable", value: true }));
    const response = yield call(apis.getDriverPoints, payload);

    const meta = {
      currentPage: +response.headers["current-page"],
      count: +response.headers["total-count"],
      pages: +response.headers["total-pages"],
    };
    const points = response.data?.points_details;
    yield put(getDriverPointsListResponse({ points, meta }));
    yield put(setSkeletonObject({ field: "pointsTable", value: false }));
    if (points.length === 0) {
      yield put(setCanShowEmptyState(true));
    } else {
      yield put(setCanShowEmptyState(false));
    }
  } catch (err) {
    yield put(setSkeletonObject({ field: "pointsTable", value: false }));
    yield put(setCanShowEmptyState(true));
    yield err.response?.status !== 401 &&
      showSnackMsg(err.response?.data?.message);
  }
}

export function* getDriverRewardsListSaga({ payload }) {
  try {
    yield put(getDriverRewardsListResponse({}));
    yield put(setSkeletonObject({ field: "rewardsTable", value: true }));
    const response = yield call(apis.getDriverRewards, payload);

    const meta = {
      currentPage: +response.headers["current-page"],
      count: +response.headers["total-count"],
      pages: +response.headers["total-pages"],
    };
    const rewards = response.data?.driver_rewards;
    yield put(getDriverRewardsListResponse({ rewards, meta }));
    yield put(setSkeletonObject({ field: "rewardsTable", value: false }));
    if (rewards.length === 0) {
      yield put(setCanShowEmptyState(true));
    } else {
      yield put(setCanShowEmptyState(false));
    }
  } catch (err) {
    yield put(setSkeletonObject({ field: "rewardsTable", value: false }));
    yield put(setCanShowEmptyState(true));
    yield err.response?.status !== 401 &&
      showSnackMsg(err.response?.data?.message);
  }
}

export function* editUserSaga({ payload }) {
  const { callUsersList, listParams, msg } = payload;
  try {
    yield put(
      setSkeletonObject({
        field: callUsersList ? "table" : "userDetails",
        value: true,
      })
    );
    // TODO: if there is edit owner in the future then check for role beforeadding api
    const response = yield call(apis.editDriver, payload);
    const user = response.data?.driver;
    yield put(getUserDetailsResponse(user));
    yield put(
      setSkeletonObject({
        field: callUsersList ? "table" : "userDetails",
        value: false,
      })
    );
    yield put(
      getDriverRequestsListRequest({
        page: 1,
        driver_id: payload.id,
      })
    );
    if (callUsersList) {
      yield put(getUsersListRequest(listParams));
    }
    yield showSnackMsg(msg, "success");
  } catch (err) {
    yield put(
      setSkeletonObject({
        field: callUsersList ? "table" : "userDetails",
        value: false,
      })
    );
    yield err.response?.status !== 401 &&
      showSnackMsg(err.response?.data?.message);
  }
}

export function* bulkEditDriversSaga({ payload }) {
  const { listParams, msg } = payload;
  try {
    yield put(setSkeletonObject({ field: "table", value: true }));
    yield call(apis.bulkEditDrivers, payload.data);
    yield put(setSkeletonObject({ field: "table", value: false }));
    yield put(getUsersListRequest(listParams));
    yield showSnackMsg(msg, "success");
  } catch (err) {
    yield put(setSkeletonObject({ field: "table", value: false }));
    yield err.response?.status !== 401 &&
      showSnackMsg(err.response?.data?.message);
  }
}

export function* qualifyDriverByPhoneNumberSaga({ payload }) {
  try {
    const { data, successMsg, listParams } = payload;
    yield put(setSkeletonObject({ field: "table", value: true }));
    yield call(apis.qualifyDriverByPhoneNumber, data);
    yield put(setSkeletonObject({ field: "table", value: false }));
    yield showSnackMsg(successMsg, "success");
    yield put(getUsersListRequest(listParams));
  } catch (err) {
    yield put(setSkeletonObject({ field: "table", value: false }));
    yield err.response?.status !== 401 &&
      showSnackMsg(err.response?.data?.message);
  }
}

export function* qualifyDriversBySheetSaga({ payload }) {
  try {
    const { data, successMsg, listParams } = payload;
    yield put(setSkeletonObject({ field: "table", value: true }));
    const response = yield call(apis.qualifyDriversBySheet, data);
    if (response.status === 200) {
      const updatedSuccessMsg = successMsg.replace(
        ":count",
        response.data.qualified_phone_numbers_count
      );
      yield showSnackMsg(updatedSuccessMsg, "success");
      yield put(setSkeletonObject({ field: "table", value: false }));
    }

    if (response.status === 207) {
      yield put(
        setRejectedDriversData({
          modalOpen: true,
          driversData: response.data.message,
          qualifiedDriversCount: response.data.qualified_phone_numbers_count,
        })
      );
      yield put(setSkeletonObject({ field: "table", value: false }));
    }
    yield put(getUsersListRequest(listParams));
  } catch (err) {
    yield put(setSkeletonObject({ field: "table", value: false }));
    yield err.response?.status !== 401 &&
      showSnackMsg(err.response?.data?.message);
  }
}

export function* getPendingDriversSaga({ payload }) {
  try {
    yield put(getPendingDriversResponse({}));
    yield put(setSkeletonObject({ field: "table", value: true }));
    const response = yield call(apis.getPendingDrivers, payload);

    const meta = {
      totalUsers: response.headers["total-count"],
      currentPage: +response.headers["current-page"],
      count: +response.headers["total-count"],
      pages: +response.headers["total-pages"],
      totalQualifiedDrivers: response.data.qualified_total_count,
      totalStandardDrivers: response.data.not_qualified_total_count,
    };
    const users = response.data?.manually_imported_drivers;
    yield put(getPendingDriversResponse({ users, meta }));
    yield put(setSkeletonObject({ field: "table", value: false }));
    if (users.length === 0) {
      yield put(setCanShowEmptyState(true));
    } else {
      yield put(setCanShowEmptyState(false));
    }
  } catch (err) {
    yield put(setSkeletonObject({ field: "table", value: false }));
    yield put(setCanShowEmptyState(true));
    yield err.response?.status !== 401 &&
      showSnackMsg(err.response?.data?.message);
  }
}

export function* editPendingDriverSaga({ payload }) {
  const { listParams, msg } = payload;
  try {
    yield put(
      setSkeletonObject({
        field: "table",
        value: true,
      })
    );
    yield call(apis.editPendingDriver, payload);
    yield put(
      setSkeletonObject({
        field: "table",
        value: false,
      })
    );
    yield put(getPendingDriversRequest(listParams));
    yield showSnackMsg(msg, "success");
  } catch (err) {
    yield put(
      setSkeletonObject({
        field: "table",
        value: false,
      })
    );
    yield err.response?.status !== 401 &&
      showSnackMsg(err.response?.data?.message);
  }
}

export function* getDriverRequestsListSaga({ payload }) {
  try {
    yield put(getDriverRequestsListResponse({}));
    yield put(setSkeletonObject({ field: "requestsTable", value: true }));
    const response = yield call(apis.getDriverRequests, payload);

    const meta = {
      currentPage: +response.headers["current-page"],
      count: +response.headers["total-count"],
      pages: +response.headers["total-pages"],
    };
    const requests = response.data?.verification_requests;
    yield put(getDriverRequestsListResponse({ requests, meta }));
    yield put(setSkeletonObject({ field: "requestsTable", value: false }));
    if (requests.length === 0) {
      yield put(setCanShowEmptyState(true));
    } else {
      yield put(setCanShowEmptyState(false));
    }
  } catch (err) {
    yield put(setSkeletonObject({ field: "requestsTable", value: false }));
    yield put(setCanShowEmptyState(true));
    yield err.response?.status !== 401 &&
      showSnackMsg(err.response?.data?.message);
  }
}

export function* updateDriverRequestSaga({ payload }) {
  try {
    yield put(setSkeletonObject({ field: "requestsTable", value: true }));
    yield call(apis.updateDriverRequest, payload);
    yield put(
      getDriverRequestsListRequest({
        page: 1,
        driver_id: payload.driverId,
      })
    );
    yield put(
      getUserDetailsRequest({
        id: payload.driverId,
        role: USERS_ROLES.driver,
      })
    );
    yield put(setSkeletonObject({ field: "requestsTable", value: false }));
  } catch (err) {
    yield put(setSkeletonObject({ field: "requestsTable", value: false }));
    yield put(setCanShowEmptyState(true));
    yield err.response?.status !== 401 &&
      showSnackMsg(err.response?.data?.message);
  }
}

export function* getReferralRewardsListSaga({ payload }) {
  try {
    yield put(getReferralRewardsListResponse({}));
    yield put(
      setSkeletonObject({ field: "referralRewardstable", value: true })
    );
    const response = yield call(apis.getReferralRewardsRequests, payload);
    const meta = {
      currentPage: +response.headers["current-page"],
      count: +response.headers["total-count"],
      pages: +response.headers["total-pages"],
    };
    const referralRewards = response.data?.referrals;
    yield put(getReferralRewardsListResponse({ referralRewards, meta }));
    yield put(
      setSkeletonObject({ field: "referralRewardstable", value: false })
    );
    if (referralRewards.length === 0) {
      yield put(setCanShowEmptyState(true));
    } else {
      yield put(setCanShowEmptyState(false));
    }
  } catch (err) {
    yield put(
      setSkeletonObject({ field: "referralRewardstable", value: false })
    );
    yield put(setCanShowEmptyState(true));
    yield err.response?.status !== 401 &&
      showSnackMsg(err.response?.data?.message);
  }
}

export function* verifyDriverLicenseSaga({ payload }) {
  try {
    yield put(setSkeletonObject({ field: "requestsTable", value: true }));
    if (payload.preventVerifyLicense) {
      yield put(
        verifyDriverLicenseResponse({
          licenseNumber: payload.data.license_number,
          currentlyExist: false,
          previouslyExist: false,
          isVerified: true,
        })
      );
    } else {
      const response = yield call(apis.verifyDriverLicense, payload.data);
      if (response.status === 200) {
        yield put(
          verifyDriverLicenseResponse({
            licenseNumber: payload.data.license_number,
            currentlyExist: false,
            previouslyExist: false,
            isVerified: true,
          })
        );
      } else if (response.status === 202) {
        yield put(
          verifyDriverLicenseResponse({
            licenseNumber: payload.data.license_number,
            currentlyExist: false,
            previouslyExist: true,
            isVerified: false,
          })
        );
      }
    }
    yield put(setSkeletonObject({ field: "requestsTable", value: false }));
  } catch (err) {
    yield put(setSkeletonObject({ field: "requestsTable", value: false }));
    yield put(setCanShowEmptyState(true));
    if (err.response?.status === 409) {
      yield put(
        verifyDriverLicenseResponse({
          licenseNumber: payload.data.license_number,
          currentlyExist: true,
          previouslyExist: false,
          isVerified: false,
        })
      );
    }

    yield ![409, 401].includes(err.response?.status) &&
      showSnackMsg(err.response?.data?.message);
  }
}

export function* getVehiclesListSaga({ payload }) {
  try {
    yield put(getVehiclesListResponse({}));
    yield put(
      setSkeletonObject({ field: "vehiclesTable", value: true })
    );
    const response = yield call(apis.getVehicles, { id: payload.id, params: payload.params });
    const meta = {
      currentPage: +response.headers["current-page"],
      count: +response.headers["total-count"],
      pages: +response.headers["total-pages"],
    };
    const vehicles = response.data?.vehicles;
    yield put(getVehiclesListResponse({ vehicles, meta }));
    yield put(
      setSkeletonObject({ field: "vehiclesTable", value: false })
    );
    if (vehicles.length === 0) {
      yield put(setCanShowEmptyState(true));
    } else {
      yield put(setCanShowEmptyState(false));
    }
  } catch (err) {
    yield put(
      setSkeletonObject({ field: "vehiclesTable", value: false })
    );
    yield put(setCanShowEmptyState(true));
    yield err.response?.status !== 401 &&
      showSnackMsg(err.response?.data?.message);
  }
}

export function* flagUserSaga({ payload }) {
  const { id, data, msg, refetchAction } = payload;
  try {
    yield call(apis.flagUserAsFraud, { id, data });
    if (refetchAction) yield put(refetchAction);
    yield showSnackMsg(msg, "success");
  } catch (err) {
    yield err.response?.status !== 401 &&
      showSnackMsg(err.response?.data?.message);
  }
}

export default function* UserSagas() {
  yield takeEvery("user/getUsersListRequest", getUsersListSaga);
  yield takeEvery("user/getUserDetailsRequest", getUserDetailsSaga);
  yield takeEvery("user/editUserRequest", editUserSaga);
  yield takeEvery("user/getTeamDriversListRequest", getTeamDriversListSaga);
  yield takeEvery("user/getDriverPointsListRequest", getDriverPointsListSaga);
  yield takeEvery("user/getDriverRewardsListRequest", getDriverRewardsListSaga);
  yield takeEvery("user/bulkEditDriversRequest", bulkEditDriversSaga);
  yield takeEvery(
    "user/qualifyDriverByPhoneNumberRequest",
    qualifyDriverByPhoneNumberSaga
  );
  yield takeEvery(
    "user/qualifyDriversBySheetRequest",
    qualifyDriversBySheetSaga
  );
  yield takeEvery("user/getPendingDriversRequest", getPendingDriversSaga);
  yield takeEvery("user/editPendingDriverRequest", editPendingDriverSaga);
  yield takeEvery(
    "user/getDriverRequestsListRequest",
    getDriverRequestsListSaga
  );
  yield takeEvery("user/updateDriverRequest", updateDriverRequestSaga);
  yield takeEvery(
    "user/getReferralRewardsListRequest",
    getReferralRewardsListSaga
  );
  yield takeEvery("user/verifyDriverLicenseRequest", verifyDriverLicenseSaga);
  yield takeEvery("user/getVehiclesListRequest", getVehiclesListSaga);
  yield takeEvery("user/flagUserRequest", flagUserSaga);
}
